(function runTitanPlayerScan() {
  'use strict';
  if (window.__TITAN_PLAYER_SCAN__) return;
  const root = document.querySelector('[data-tps-root]');
  if (!root) return;
  const config = window.TitanPlayerScanConfig || {};
  const runtime = window.TitanScanRuntime;
  const data = window.TitanScanData;
  const logic = window.TitanScanLogic;
  const aiService = window.TitanScanAI;
  const effects = window.TitanScanEffects;
  const testRuntime = window.TitanScanTestRuntime;
  const shellView = window.TitanScanShellView;
  const session = window.TitanScanSession;
  const inputView = window.TitanScanInputView;
  const resultView = window.TitanScanResultView;
  const refsFactory = window.TitanScanRefs;
  const storageKey = runtime?.storageKey(config) || 'titan-player-scan:v2';
  const fallbackProductImage = runtime?.asset(config, 'frame2', 'assets/frame2.jpg');
  const publicResultBase = runtime?.publicResultUrl(config.publicResultUrl, window.location.origin);
  const testScreens = new Set(['input-test', 'reaction-test']);
  const transientScreens = new Set(['input-test', 'reaction-test', 'analyzing']);
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const baseDocumentTitle = document.title;
  const testTimingOverrides = window.__TITAN_SCAN_TEST_TIMINGS__ || {};
  const timings = Object.freeze({
    countdownStepMs: validTiming(testTimingOverrides.countdownStepMs, 1000),
    mouseInputMs: validTiming(testTimingOverrides.mouseInputMs, 15000),
    touchInputMs: validTiming(testTimingOverrides.touchInputMs, 10000),
    reactionDelayMin: validTiming(testTimingOverrides.reactionDelayMin, 1600),
    reactionDelayMax: validTiming(testTimingOverrides.reactionDelayMax, 3800),
    reactionResultHold: validTiming(testTimingOverrides.reactionResultHold, 620),
    analysisDuration: validTiming(testTimingOverrides.analysisDuration, 1800),
  });
  function validTiming(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) && number >= 20 ? number : fallback;
  }
  const byId = (id) => root.querySelector(`#${id}`);
  const refs = refsFactory?.collect(root);
  let state = null;
  let currentResult = null;
  let sound = null;
  let radar = null;
  let videoController = null;
  let dialogBoundary = null;
  let countdown = null;
  let inputSession = null;
  let inputGeometry = null;
  let inputTargetPosition = null;
  let inputFrame = null;
  let testSetupFrame = null;
  let reactionSession = null;
  let reactionTimer = null;
  let analysisAbort = null;
  let messageTimer = null;
  let countCancels = [];
  let resizeFrame = null;
  let ownsInputFullscreen = false;
  let storageEnabled = true;
  let storageWarningShown = false;
  const createInitialState = () => session.createInitialState(data, logic, session.getDeviceSignals());
  function saveState(currentState) {
    if (!storageEnabled) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(session.storageSnapshot(currentState)));
    } catch (error) {
      storageEnabled = false;
      if (!storageWarningShown) {
        storageWarningShown = true;
        announce('このブラウザでは途中保存を利用できません。診断はそのまま続けられます。', 5000);
      }
    }
  }
  function loadStoredState(initialState) {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const restored = session.restoreStoredState(raw, initialState, logic, data);
      if (!restored) {
        localStorage.removeItem(storageKey);
        return null;
      }
      return restored;
    } catch (error) {
      try {
        localStorage.removeItem(storageKey);
      } catch (storageError) {
        storageEnabled = false;
      }
      return null;
    }
  }
  const stateFromHash = (hash, initialState) => session.stateFromHash(hash, initialState, logic, data);
  function announce(message, duration = 2600) {
    if (!refs.appMessage) return;
    window.clearTimeout(messageTimer);
    refs.appMessage.textContent = message;
    if (duration > 0) {
      messageTimer = window.setTimeout(() => {
        refs.appMessage.textContent = '';
      }, duration);
    }
  }
  function clearMessage() {
    window.clearTimeout(messageTimer);
    refs.appMessage.textContent = '';
  }
  function shareHash(currentState = state) {
    return logic.encodeResultHash(currentState, data);
  }
  function urlForState(currentState) {
    const url = new URL(window.location.href);
    url.hash = '';
    if (session.persistableScreen(currentState) === 'result') {
      url.hash = shareHash(currentState);
    }
    return url.href;
  }
  function updateHistory(mode, currentState) {
    if (mode === 'none') return;
    try {
      const payload = { titanPlayerScan: true, screen: session.persistableScreen(currentState) };
      history[mode === 'push' ? 'pushState' : 'replaceState'](
        payload,
        '',
        urlForState(currentState),
      );
    } catch (error) {
      announce('ブラウザ履歴を更新できませんでした。診断は続けられます。', 4000);
    }
  }
  function commit(nextState, options = {}) {
    const previousScreen = state ? state.screen : null;
    state = session.freezeState(nextState);
    if (options.persist !== false) saveState(state);
    updateHistory(options.history || 'none', state);
    render(previousScreen, options.focus !== false);
  }
  function canNavigate(screen) {
    if (['intro', 'game'].includes(screen)) return true;
    if (screen === 'profile') return Boolean(state.answers.game);
    if (screen === 'preferences') return Boolean(state.answers.style && state.answers.monitorHz);
    if (screen === 'input-intro') {
      return Boolean(state.answers.priority && state.answers.streaming);
    }
    if (screen === 'reaction-intro') return state.completed.input;
    if (screen === 'result') return state.completed.reaction && state.metrics.reactions.length === 3;
    return transientScreens.has(screen);
  }
  function navigate(screen, historyMode = 'push') {
    if (!data.screens.includes(screen) || !canNavigate(screen)) {
      announce('この画面へ進むための入力が不足しています。');
      return;
    }
    cancelRuntime();
    clearMessage();
    commit({ ...state, screen }, { history: historyMode });
  }
  function render(previousScreen, shouldFocus) {
    const isTest = testScreens.has(state.screen);
    if (!isTest) dialogBoundary?.deactivate();
    document.documentElement.classList.toggle('tps-v2-is-testing', isTest);
    document.documentElement.classList.toggle('tps-v2-mode-touch', state.device.mode === 'touch');
    document.body.classList.toggle('tps-v2-is-testing', isTest);
    document.body.classList.toggle('tps-v2-mode-touch', state.device.mode === 'touch');
    refs.screens.forEach((screen) => {
      const active = screen.id === state.screen;
      screen.hidden = !active;
      screen.classList.toggle('active', active);
    });
    renderProgress();
    shellView.renderControls(refs, state, logic);
    renderInputIntro();
    shellView.renderSound(refs, state);
    if (state.screen === 'result') populateResult();
    videoController?.sync();
    if (previousScreen !== state.screen) {
      document.title = state.screen === 'result' && currentResult
        ? `${currentResult.playerClass} — TITAN PLAYER SCAN`
        : baseDocumentTitle;
      if (state.screen === 'result') animateResult();
      if (shouldFocus) shellView.focusScreen(root, state.screen, testScreens);
    }
  }
  function renderProgress() {
    const activeScreen = byId(state.screen);
    const step = activeScreen ? Number(activeScreen.dataset.step) || 1 : 1;
    refs.stepText.textContent = `STEP ${String(step).padStart(2, '0')} / 07`;
    refs.progressBar.style.transform = `scaleX(${step / 7})`;
    refs.progress.setAttribute('aria-valuenow', String(step));
  }
  function renderInputIntro() {
    inputView.renderIntro(refs, state);
  }
  function updateAnswers(patch) {
    commit({ ...state, answers: { ...state.answers, ...patch } }, { focus: false });
  }
  function measureInputArena() {
    inputGeometry = testRuntime.measureArena(refs.inputArena, refs.inputTarget);
  }
  function moveTarget() {
    if (!inputSession || !inputGeometry || state.screen !== 'input-test') return;
    inputTargetPosition = testRuntime.nextTargetPosition(
      inputGeometry,
      state.device.mode === 'touch',
    );
    refs.inputTarget.style.transform = `translate3d(${Math.round(inputTargetPosition.x)}px, ${Math.round(inputTargetPosition.y)}px, 0)`;
  }
  const inputDuration = () => state.device.mode === 'touch' ? timings.touchInputMs : timings.mouseInputMs;
  async function releaseInputFullscreen() {
    if (!ownsInputFullscreen) return;
    ownsInputFullscreen = false;
    if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
      try {
        await document.exitFullscreen();
      } catch (error) {
        // The browser may already be leaving fullscreen after Escape.
      }
    }
  }
  async function startInputTest() {
    cancelRuntime();
    const mode = state.device.mode;
    refs.inputViewportWarning.hidden = true;
    if (mode === 'mouse' && !document.fullscreenElement && typeof document.documentElement.requestFullscreen === 'function') {
      try {
        await document.documentElement.requestFullscreen();
        ownsInputFullscreen = true;
      } catch (error) {
        ownsInputFullscreen = false;
      }
    }
    if (!testRuntime.canStartInputTest(mode, window.innerWidth, window.innerHeight)) {
      refs.inputViewportWarning.hidden = false;
      refs.inputViewportWarning.focus?.({ preventScroll: true });
      announce('AIMテストはウィンドウを1100×650ピクセル以上に広げてください。', 6000);
      void releaseInputFullscreen();
      return;
    }
    commit(
      {
        ...state,
        screen: 'input-test',
        metrics: session.emptyMetrics(mode),
        completed: { input: false, reaction: false },
      },
      { focus: false },
    );
    dialogBoundary.activate(refs.inputTest, refs.startInput, abortActiveTest);
    refs.inputTarget.disabled = true;
    refs.inputTestCancel.focus({ preventScroll: true });
    countdown.start({
      overlay: refs.inputCountdown,
      value: refs.inputCountdownValue,
      live: refs.inputCountdownLive,
      stepMs: timings.countdownStepMs,
      onComplete: () => {
        if (state.screen !== 'input-test') return;
        testSetupFrame = window.requestAnimationFrame(() => {
          testSetupFrame = null;
          if (state.screen !== 'input-test') return;
          measureInputArena();
          inputSession = Object.freeze({
            startAt: performance.now(),
            durationMs: inputDuration(),
            hits: 0,
            misses: 0,
            hitTimestamps: Object.freeze([]),
          });
          updateInputHud(inputSession);
          moveTarget();
          refs.inputTarget.disabled = false;
          refs.inputTarget.focus({ preventScroll: true });
          inputFrame = requestAnimationFrame(tickInputTest);
        });
      },
    });
  }
  function tickInputTest(now) {
    if (!inputSession || state.screen !== 'input-test') return;
    const remaining = Math.max(0, inputSession.durationMs - (now - inputSession.startAt));
    const displayTime = (remaining / 1000).toFixed(1);
    if (refs.inputTime.textContent !== displayTime) refs.inputTime.textContent = displayTime;
    if (remaining > 0) {
      inputFrame = requestAnimationFrame(tickInputTest);
      return;
    }
    finishInputTest();
  }
  function recordInputHit(localX, localY) {
    if (!inputSession || !inputGeometry) return;
    const now = performance.now();
    const nextPosition = testRuntime.nextTargetPosition(
      inputGeometry,
      state.device.mode === 'touch',
    );
    inputSession = Object.freeze({
      ...inputSession,
      hits: inputSession.hits + 1,
      hitTimestamps: Object.freeze([...inputSession.hitTimestamps, now]),
    });
    sound.play('hit');
    effects.spawnHitEffect(refs.inputArena, localX, localY, reducedMotionQuery.matches);
    updateInputHud(inputSession);
    inputTargetPosition = nextPosition;
    refs.inputTarget.style.transform = `translate3d(${Math.round(nextPosition.x)}px, ${Math.round(nextPosition.y)}px, 0)`;
  }
  function recordInputMiss() {
    if (!inputSession) return;
    inputSession = Object.freeze({ ...inputSession, misses: inputSession.misses + 1 });
    updateInputHud(inputSession);
  }
  function updateInputHud(session) {
    const touchMode = state.device.mode === 'touch';
    const score = touchMode
      ? logic.scoreTap({
          hitTimestamps: session.hitTimestamps,
          misses: session.misses,
          durationMs: session.durationMs,
        })
      : logic.scoreAim({ hits: session.hits, misses: session.misses, durationMs: session.durationMs });
    refs.inputHitLabel.textContent = touchMode ? 'TAPS' : 'HITS';
    refs.inputHits.textContent = String(session.hits);
    refs.inputAccuracy.textContent = session.hits + session.misses === 0 ? '—' : `${score.accuracy}%`;
    refs.inputExtraLabel.textContent = touchMode ? 'AVG' : 'MISSES';
    refs.inputExtra.textContent = touchMode
      ? score.averageInterval === null ? '—' : `${score.averageInterval}ms`
      : String(session.misses);
  }
  function finishInputTest() {
    if (!inputSession) return;
    const session = inputSession;
    cancelAnimationFrame(inputFrame);
    inputFrame = null;
    inputSession = null;
    inputGeometry = null;
    inputTargetPosition = null;
    void releaseInputFullscreen();
    const touchMode = state.device.mode === 'touch';
    const score = touchMode
      ? logic.scoreTap({
          hitTimestamps: session.hitTimestamps,
          misses: session.misses,
          durationMs: session.durationMs,
        })
      : logic.scoreAim({ hits: session.hits, misses: session.misses, durationMs: session.durationMs });
    const metrics = {
      mode: state.device.mode,
      hits: touchMode ? score.taps : score.hits,
      misses: score.misses,
      accuracy: score.accuracy,
      inputScore: score.score,
      averageInterval: touchMode ? score.averageInterval : null,
      fastestInterval: touchMode ? score.fastestInterval : null,
      reactions: [],
    };
    commit(
      {
        ...state,
        screen: 'reaction-intro',
        metrics,
        completed: { input: true, reaction: false },
      },
      { history: 'push' },
    );
  }
  function startReactionTest() {
    cancelRuntime();
    if (!state.preferences.muted) void sound.setMuted(false);
    commit(
      {
        ...state,
        screen: 'reaction-test',
        metrics: { ...state.metrics, reactions: [] },
        completed: { ...state.completed, reaction: false },
      },
      { focus: false },
    );
    dialogBoundary.activate(refs.reactionTest, refs.startReaction, abortActiveTest);
    refs.reactionArea.disabled = true;
    refs.reactionTestCancel.focus({ preventScroll: true });
    countdown.start({
      overlay: refs.reactionCountdown,
      value: refs.reactionCountdownValue,
      live: refs.reactionCountdownLive,
      stepMs: timings.countdownStepMs,
      onComplete: () => {
        if (state.screen !== 'reaction-test') return;
        refs.reactionArea.disabled = false;
        refs.reactionArea.focus({ preventScroll: true });
        prepareReactionRound(1, []);
      },
    });
  }
  function setReactionDisplay(status, message, submessage, round) {
    refs.reactionArea.className = `reaction-area ${status}`;
    refs.reactionMessage.textContent = message;
    refs.reactionSubmessage.textContent = submessage;
    refs.reactionRound.textContent = `${round} / 3`;
    refs.reactionLive.textContent = `${message}。${submessage}。ラウンド ${round} / 3`;
  }
  function prepareReactionRound(round, samples) {
    window.clearTimeout(reactionTimer);
    reactionSession = Object.freeze({
      status: 'waiting',
      round,
      samples: Object.freeze([...samples]),
      readyAt: 0,
      lastActionAt: 0,
    });
    setReactionDisplay('waiting', 'WAIT', '緑になったらクリック', round);
    const delay = timings.reactionDelayMin + Math.random() * Math.max(0, timings.reactionDelayMax - timings.reactionDelayMin);
    reactionTimer = window.setTimeout(() => {
      if (!reactionSession || reactionSession.status !== 'waiting') return;
      reactionSession = Object.freeze({ ...reactionSession, status: 'ready', readyAt: performance.now() });
      setReactionDisplay('ready', 'CLICK', '今すぐクリック', round);
      sound.play('ready');
    }, delay);
  }
  function handleReactionAction(actionAt = performance.now()) {
    if (!reactionSession) return;
    const now = actionAt;
    if (now - reactionSession.lastActionAt < 120) return;
    reactionSession = Object.freeze({ ...reactionSession, lastActionAt: now });
    if (reactionSession.status === 'waiting') {
      window.clearTimeout(reactionTimer);
      reactionSession = Object.freeze({ ...reactionSession, status: 'too-early' });
      setReactionDisplay('too-early', 'TOO SOON', 'クリックして同じラウンドを再試行', reactionSession.round);
      sound.play('early');
      announce('早押しです。記録には入りません。', 1800);
      return;
    }
    if (reactionSession.status === 'too-early') {
      prepareReactionRound(reactionSession.round, reactionSession.samples);
      return;
    }
    if (reactionSession.status !== 'ready') return;
    const milliseconds = Math.max(1, Math.round(now - reactionSession.readyAt));
    const samples = [...reactionSession.samples, milliseconds];
    reactionSession = Object.freeze({ ...reactionSession, status: 'done', samples: Object.freeze(samples) });
    setReactionDisplay('waiting', `${milliseconds} ms`, reactionSession.round < 3 ? '次のラウンドを準備中' : '測定完了', reactionSession.round);
    reactionTimer = window.setTimeout(() => {
      if (reactionSession.round < 3) prepareReactionRound(reactionSession.round + 1, samples);
      else startAnalysis(samples);
    }, timings.reactionResultHold);
  }
  async function startAnalysis(samples) {
    cancelReactionTimer();
    commit(
      {
        ...state,
        screen: 'analyzing',
        metrics: { ...state.metrics, reactions: samples },
        completed: { input: true, reaction: true },
      },
      { focus: true },
    );
    const duration = reducedMotionQuery.matches ? 30 : timings.analysisDuration;
    refs.analysisStatus.textContent = 'ゲーム負荷を照合しています';
    const controller = new AbortController();
    analysisAbort = controller;
    try {
      const selection = aiService
        ? await aiService.recommend(state, data, {
            signal: controller.signal,
            minimumDelayMs: duration,
            onStatus: (message) => { refs.analysisStatus.textContent = message; },
            testProductId: window.__TITAN_SCAN_TEST_AI_PRODUCT_ID__,
          })
        : await new Promise((resolve) => window.setTimeout(() => resolve(null), duration));
      if (controller.signal.aborted || state.screen !== 'analyzing') return;
      const resultState = { ...state, recommendedProductId: selection?.productId || null };
      currentResult = logic.buildDiagnosis(resultState, data);
      sound.play('complete');
      commit({ ...resultState, screen: 'result' }, { history: 'push' });
    } catch (error) {
      if (controller.signal.aborted) return;
      try {
        const fallbackState = { ...state, recommendedProductId: null };
        currentResult = logic.buildDiagnosis(fallbackState, data);
        commit({ ...fallbackState, screen: 'result' }, { history: 'push' });
      } catch (fallbackError) {
        announce('診断結果を作成できませんでした。反応速度テストを再実行してください。', 6000);
        commit({ ...state, screen: 'reaction-intro', completed: { ...state.completed, reaction: false } });
      }
    } finally {
      if (analysisAbort === controller) analysisAbort = null;
    }
  }
  function cancelReactionTimer() { window.clearTimeout(reactionTimer); reactionTimer = null; reactionSession = null; }
  function cancelRuntime() {
    countdown?.cancel();
    if (testSetupFrame !== null) cancelAnimationFrame(testSetupFrame);
    testSetupFrame = null;
    if (inputFrame !== null) cancelAnimationFrame(inputFrame);
    inputFrame = null;
    inputSession = null;
    inputGeometry = null;
    inputTargetPosition = null;
    cancelReactionTimer();
    if (refs) {
      refs.inputTarget.disabled = false;
      refs.reactionArea.disabled = false;
    }
    analysisAbort?.abort();
    analysisAbort = null;
  }
  function abortActiveTest() {
    if (!testScreens.has(state.screen)) return;
    const inputActive = state.screen === 'input-test';
    const screen = inputActive ? 'input-intro' : 'reaction-intro';
    const metrics = inputActive
      ? session.emptyMetrics(state.device.mode)
      : { ...state.metrics, reactions: [] };
    const completed = inputActive
      ? { input: false, reaction: false }
      : { input: true, reaction: false };
    dialogBoundary.deactivate({ restoreFocus: true });
    cancelRuntime();
    if (inputActive) void releaseInputFullscreen();
    commit({ ...state, screen, metrics, completed }, { history: 'replace', focus: false });
    announce('テストを中止しました。再開すると最初から計測します。', 5000);
  }
  function populateResult() {
    try {
      currentResult = logic.buildDiagnosis(state, data);
    } catch (error) {
      currentResult = null;
      announce('保存された結果を復元できませんでした。最初から診断してください。', 6000);
      return;
    }
    resultView.render(refs, state, currentResult, data, logic);
    prepareResultShare();
  }

  function prepareResultShare() {
    const result = currentResult;
    refs.shareResult.disabled = true;
    refs.saveResultImage.disabled = true;
    effects.prepareShareCard(refs.shareCanvas, result)
      .then(() => {
        if (state.screen !== 'result' || currentResult !== result) return;
        refs.shareResult.disabled = false;
        refs.saveResultImage.disabled = false;
      })
      .catch(() => {
        if (state.screen !== 'result' || currentResult !== result) return;
        refs.saveResultImage.disabled = false;
        announce('共有カードの事前準備に失敗しました。PNG保存からもう一度お試しください。', 5000);
      });
  }

  function animateResult() {
    if (!currentResult) return;
    countCancels.forEach((cancel) => cancel());
    countCancels = [
      effects.animateNumber(refs.inputScore, currentResult.inputScore, { reducedMotion: reducedMotionQuery.matches }),
      effects.animateNumber(refs.reactionScore, currentResult.reactionAverage, { reducedMotion: reducedMotionQuery.matches }),
      effects.animateNumber(refs.accuracyScore, currentResult.accuracy, { reducedMotion: reducedMotionQuery.matches }),
    ];
    radar.render(currentResult.radar.values, currentResult.radar.labels, reducedMotionQuery.matches);
  }

  function resultUrl() {
    const hash = shareHash();
    if (!publicResultBase) return hash;
    const url = new URL(publicResultBase.href);
    url.hash = hash;
    return url.href;
  }

  async function handleShare() {
    if (!currentResult) return;
    refs.shareResult.disabled = true;
    refs.shareResult.setAttribute('aria-busy', 'true');
    try {
      const outcome = await effects.nativeShareCard(refs.shareCanvas, currentResult, resultUrl());
      if (outcome === 'shared-file') announce('結果カードを共有しました。');
      if (outcome === 'shared-text') announce('この端末では画像を添付できないため、結果文を共有しました。', 5000);
      if (outcome === 'preparing') announce('結果カードを準備しています。少し待ってからもう一度お試しください。', 4000);
      if (outcome === 'unsupported') announce('端末共有に対応していません。PNG保存を利用してください。', 5000);
      if (outcome === 'failed') announce('共有先を開けませんでした。PNG保存を利用してください。', 5000);
    } catch (error) {
      announce('結果カードを作成できませんでした。PNG保存をもう一度お試しください。', 5000);
    } finally {
      refs.shareResult.disabled = false;
      refs.shareResult.removeAttribute('aria-busy');
    }
  }

  function openSocialShare(destination) {
    if (!currentResult) return;
    const target = effects.buildSocialShareUrl(destination, currentResult, resultUrl());
    window.open(target, '_blank', 'noopener,noreferrer');
  }

  async function handleSaveResultImage() {
    if (!currentResult) return;
    refs.saveResultImage.disabled = true;
    refs.saveResultImage.setAttribute('aria-busy', 'true');
    try {
      await effects.downloadShareCard(refs.shareCanvas, currentResult);
      announce('共有用PNGを保存しました。');
    } catch (error) {
      announce('PNGを保存できませんでした。スクリーンショットで保存してください。', 5000);
    } finally {
      refs.saveResultImage.disabled = false;
      refs.saveResultImage.removeAttribute('aria-busy');
    }
  }

  async function handleCopyUrl() {
    const url = resultUrl();
    const copied = await effects.copyText(url);
    const successMessage = url.startsWith('#')
      ? '結果コードをコピーしました。公開後は結果URLとして共有できます。'
      : '結果URLをコピーしました。';
    announce(copied ? successMessage : 'URLをコピーできませんでした。アドレス欄からコピーしてください。', 4000);
  }
  async function toggleSound() {
    const requestedMuted = !state.preferences.muted;
    const soundState = await sound.setMuted(requestedMuted);
    const muted = soundState.available ? soundState.muted : true;
    commit({ ...state, preferences: { muted } }, { focus: false });
    if (!soundState.available && !requestedMuted) {
      announce('このブラウザでは効果音を再生できません。');
    } else if (!muted) {
      sound.play('ready');
    }
  }
  function resetScan() {
    cancelRuntime();
    countCancels.forEach((cancel) => cancel());
    countCancels = [];
    radar.cancel();
    currentResult = null;
    clearMessage();
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      storageEnabled = false;
    }
    const nextState = createInitialState();
    void sound.setMuted(true);
    commit(nextState, { history: 'replace', persist: false });
  }
  function bindAnswerEvents() {
    refs.root.querySelectorAll('[data-go]').forEach((button) => {
      button.addEventListener('click', () => navigate(button.dataset.go));
    });
    refs.gameGrid.addEventListener('click', (event) => {
      const button = event.target.closest('[data-game]');
      if (button) updateAnswers({ game: button.dataset.game });
    });
    refs.root.querySelectorAll('input[name="style"]').forEach((input) => {
      input.addEventListener('change', () => updateAnswers({ style: input.value }));
    });
    refs.root.querySelectorAll('input[name="priority"]').forEach((input) => {
      input.addEventListener('change', () => updateAnswers({ priority: input.value }));
    });
    refs.root.querySelectorAll('input[name="streaming"]').forEach((input) => {
      input.addEventListener('change', () => updateAnswers({ streaming: input.value }));
    });
    refs.monitorGrid.addEventListener('click', (event) => {
      const button = event.target.closest('[data-monitor]');
      if (button) updateAnswers({ monitorHz: Number(button.dataset.monitor) });
    });
    refs.budgetRange.addEventListener('input', () => updateAnswers({ budget: Number(refs.budgetRange.value) }));
    refs.budgetUnlimited.addEventListener('change', () => {
      updateAnswers({ budgetUnlimited: refs.budgetUnlimited.checked });
    });
  }
  function bindTestEvents() {
    refs.inputModeOverride.addEventListener('click', () => {
      const mode = state.device.mode === 'touch' ? 'mouse' : 'touch';
      commit(
        {
          ...state,
          device: { ...state.device, mode, override: mode },
          metrics: session.emptyMetrics(mode),
          completed: { input: false, reaction: false },
        },
        { focus: false },
      );
      announce(mode === 'touch' ? 'TAP SPEED TESTへ切り替えました。' : 'AIM TESTへ切り替えました。');
    });
    refs.startInput.addEventListener('click', startInputTest);
    refs.inputTestCancel.addEventListener('click', abortActiveTest);
    refs.inputTarget.addEventListener('pointerup', (event) => {
      if (event.button !== 0 || !event.isPrimary) return;
      event.preventDefault();
      event.stopPropagation();
      if (!inputGeometry) return;
      const point = testRuntime.localPoint(inputGeometry, event.clientX, event.clientY);
      recordInputHit(point.x, point.y);
    });
    refs.inputTarget.addEventListener('click', (event) => {
      if (event.detail !== 0 || !inputSession || !inputGeometry || !inputTargetPosition) return;
      const point = testRuntime.targetCenter(inputGeometry, inputTargetPosition);
      recordInputHit(point.x, point.y);
    });
    refs.inputArena.addEventListener('pointerdown', (event) => {
      if (event.button === 0 && event.isPrimary && event.target === refs.inputArena) {
        event.preventDefault();
        recordInputMiss();
      }
    });
    refs.inputArena.addEventListener('contextmenu', (event) => event.preventDefault());
    refs.startReaction.addEventListener('click', startReactionTest);
    refs.reactionTestCancel.addEventListener('click', abortActiveTest);
    refs.reactionArea.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || !event.isPrimary) return;
      event.preventDefault();
      handleReactionAction(performance.now());
    });
    refs.reactionArea.addEventListener('click', (event) => {
      if (event.detail === 0) handleReactionAction(performance.now());
    });
    refs.reactionArea.addEventListener('keydown', (event) => {
      if (event.repeat && ['Enter', ' '].includes(event.key)) event.preventDefault();
    });
  }
  function bindUiEvents() {
    refs.soundToggle.addEventListener('click', toggleSound);
    refs.resetButton.addEventListener('click', resetScan);
    refs.retryButton.addEventListener('click', resetScan);
    refs.shareResult.addEventListener('click', handleShare);
    refs.shareX.addEventListener('click', () => openSocialShare('x'));
    refs.shareLine.addEventListener('click', () => openSocialShare('line'));
    refs.saveResultImage.addEventListener('click', handleSaveResultImage);
    refs.copyResultUrl.addEventListener('click', handleCopyUrl);
    const markVideoUnavailable = () => {
      refs.root.classList.add('tps-v2-video-unavailable');
      announce('背景動画を読み込めませんでした。診断はそのまま利用できます。', 5000);
    };
    refs.heroVideo.addEventListener('error', markVideoUnavailable);
    refs.heroVideo.querySelector('source')?.addEventListener('error', markVideoUnavailable);
    if (refs.heroVideo.error) markVideoUnavailable();
    refs.productImage?.addEventListener('error', () => {
      if (refs.productImage.dataset.fallbackApplied === 'true') return;
      refs.productImage.dataset.fallbackApplied = 'true';
      refs.productImage.src = fallbackProductImage;
      refs.productImage.alt = 'TITAN Gaming PCの内部イメージ';
    });
  }
  function bindLifecycleEvents() {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden || !testScreens.has(state.screen)) return;
      const fallback = state.screen === 'input-test' ? 'input-intro' : 'reaction-intro';
      cancelRuntime();
      commit({ ...state, screen: fallback }, { focus: true });
      announce('画面が切り替わったため、計測をリセットしました。', 5000);
    });
    window.addEventListener('popstate', (event) => {
      const restored = stateFromHash(window.location.hash, createInitialState());
      if (!restored && !runtime.isScanHistoryState(event.state)) return;
      cancelRuntime();
      if (restored) {
        commit(restored, { persist: true, focus: true });
        return;
      }
      const requested = event.state.screen;
      const screen = canNavigate(requested)
        ? session.normalizeResumeScreen(requested, state.metrics)
        : 'intro';
      commit({ ...state, screen }, { persist: true });
    });
    reducedMotionQuery.addEventListener('change', () => {
      videoController.sync();
      if (state.screen === 'result' && currentResult) animateResult();
    });
    window.addEventListener('resize', () => {
      if (resizeFrame !== null) return;
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        if (state.screen === 'input-test' && state.device.mode === 'mouse' && !testRuntime.canStartInputTest('mouse', window.innerWidth, window.innerHeight)) {
          abortActiveTest();
          announce('画面が小さくなったため、AIMテストを中止しました。ウィンドウを広げて再開してください。', 6000);
        } else if (state.screen === 'input-test' && inputSession) {
          measureInputArena();
          moveTarget();
        } else if (state.screen === 'result' && currentResult) {
          radar.render(currentResult.radar.values, currentResult.radar.labels, true);
        }
      });
    });
  }
  function bindEvents() {
    bindAnswerEvents();
    bindTestEvents();
    bindUiEvents();
    bindLifecycleEvents();
  }

  function initialize() {
    if (![runtime, data, logic, effects, testRuntime, shellView, session, inputView, resultView, refsFactory, refs].every(Boolean)) {
      const appMessage = root.querySelector('#app-message');
      if (appMessage) appMessage.textContent = '診断プログラムを読み込めませんでした。ページを再読み込みしてください。';
      return;
    }
    shellView.renderGameButtons(refs.gameGrid, data.games);
    const initialState = createInitialState();
    state = stateFromHash(window.__TITAN_INITIAL_RESULT_HASH__ || window.location.hash, initialState) || loadStoredState(initialState) || initialState;
    window.__TITAN_INITIAL_RESULT_HASH__ = '';
    saveState(state);
    sound = effects.createSoundController(state.preferences.muted);
    radar = effects.createRadarRenderer(refs.radarChart);
    countdown = testRuntime.createCountdownController();
    dialogBoundary = testRuntime.createDialogBoundary();
    videoController = shellView.createVideoController(
      refs.heroVideo,
      refs.heroVideoToggle,
      refs.root,
      reducedMotionQuery,
      () => state.screen,
    );
    if (!publicResultBase) refs.copyResultUrl.textContent = '結果コードをコピー';
    refs.shareResult.hidden = !effects.canNativeShare();
    bindEvents();
    updateHistory('replace', state);
    render(null, false);
    window.__TITAN_PLAYER_SCAN__ = Object.freeze({
      getState: () => JSON.parse(JSON.stringify(state)),
      getResult: () => (currentResult ? JSON.parse(JSON.stringify(currentResult)) : null),
      getResultHash: () => (state.screen === 'result' ? shareHash() : null),
    });
  }

  initialize();
  runtime?.reloadOnSectionLoad('[data-tps-root]');
})();
